import 'react-grid-layout/css/styles.css';
import { Layout, Layouts, Responsive, WidthProvider } from 'react-grid-layout';
import { Box } from '@chakra-ui/react';
import { useState, useEffect, useMemo, useRef } from 'react';
import './grid.css';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';

import {
  deleteInstrument,
  // getDashboard,
  getSelectedPage,
  setAllInstruments,
  updatePage,
} from 'redux/DashboardSliceV2';
import { AppDispatch } from 'utils/GlobalHelpers';

import DeleteModal from './components/DeleteModal';
import Instrument from './Instrument';

export interface GridProperties {
  allInstruments: Array<any>;
}

// const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
// const cols = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const breakpoints = { lg: 480, xxs: 0 };
const cols = { lg: 12, xxs: 1 };
// react-grid-layout ties every widget's height to whole multiples of this row
// unit (height = h * rowHeight + (h-1) * marginY) — there's no per-instrument
// override, which is why a KPI card's practical floor (minH:1 in getLayout
// below) was tied to the same row unit as a table's (minH:3) and came out
// taller than the single stat line inside it needed. 100 -> 84 trims that
// floor from ~112px to ~96px without touching the CSS-height-override
// approach that broke row-packing earlier (see the useEffect below).
const rowHeight = 84;
// const rowHeightMap = { lg: 55, md: 40, sm: 60, xs: 50, xxs: 45 };

const Grid = ({ allInstruments }: GridProperties) => {
  const dispatch: AppDispatch = useDispatch();
  const selectedPage = useSelector(getSelectedPage);
  // const dashboard = useSelector(getDashboard);
  const [currentBreakpoint, setCurrentBreakpoint] = useState<keyof typeof cols>('lg');
  const [instrumentDeleteData, setInstrumentDeleteData] = useState<any>(null);
  const [savedLayout, setSavedLayout] = useState<Layouts>({});
  // Ref always holds the latest savedLayout so callbacks don't close over stale state.
  const savedLayoutReference = useRef<Layouts>(savedLayout);

  const ResponsiveReactGridLayout = useMemo(() => WidthProvider(Responsive), []);

  const getNextAvailablePosition = (
    layout: Layout[],
    columns: number,
    defaultSize = { w: 2, h: 3 },
  ) => {
    for (let y = 0; y < 20; y += 1) {
      for (let x = 0; x <= columns - defaultSize.w; x += 1) {
        const isOccupied = layout.some(
          (item) =>
            x < item.x + item.w &&
            x + defaultSize.w > item.x &&
            y < item.y + item.h &&
            y + defaultSize.h > item.y,
        );
        if (!isOccupied) {
          return { x, y, ...defaultSize };
        }
      }
    }
    return { x: 0, y: 0, ...defaultSize };
  };

  // Keep ref in sync so drag/resize callbacks always read the latest layout.
  useEffect(() => {
    savedLayoutReference.current = savedLayout;
  }, [savedLayout]);

  useEffect(() => {
    // Always reset on page change — if we don't clear when instrument_layout is absent,
    // the previous page's layout stays and new instruments auto-position on top of it.
    setSavedLayout(selectedPage?.instrument_layout || {});
  }, [selectedPage]);

  useEffect(() => {
    setSavedLayout((previousLayout: any) => {
      const currentIds = new Set(allInstruments.map((item: any) => String(item.id)));
      const updatedLayouts = { ...previousLayout };

      Object.keys(cols).forEach((breakpoint) => {
        // Drop layout items that no longer belong to this page's instruments.
        const existingLayout = (updatedLayouts[breakpoint] || []).filter((layoutItem: any) =>
          currentIds.has(layoutItem.i),
        );
        const newLayout = [...existingLayout];

        allInstruments.forEach((item) => {
          if (!existingLayout.some((layoutItem: any) => layoutItem.i === String(item.id))) {
            const position = getNextAvailablePosition(
              newLayout,
              cols[breakpoint as keyof typeof cols],
              {
                h: item.instrument_type === 'kpi' ? 1 : 3,
                w: item.instrument_type === 'table' ? 3 : 2,
              },
            );
            newLayout.push({
              i: String(item.id),
              ...position,
              minH: item.instrument_type === 'kpi' ? 1 : 3,
              minW: item.instrument_type === 'table' ? 3 : 2,
            });
          }
        });

        updatedLayouts[breakpoint] = newLayout;
      });

      return updatedLayouts;
    });
  }, [allInstruments]);

  const onResizeStop = (layout: Layout[], oldItem: Layout, newItem: Layout) => {
    if (isEqual(oldItem, newItem)) return;
    const updatedLayout = { ...savedLayoutReference.current, [currentBreakpoint]: layout };
    setSavedLayout(updatedLayout);
    dispatch(
      updatePage({
        request_body: {
          ...selectedPage,
          instrument_layout: updatedLayout,
        },
      }),
    );
  };

  const onDragStop = (layout: Layout[], oldItem: Layout, newItem: Layout) => {
    if (isEqual(oldItem, newItem)) return;
    const updatedLayout = { ...savedLayoutReference.current, [currentBreakpoint]: layout };
    setSavedLayout(updatedLayout);
    dispatch(
      updatePage({
        request_body: {
          ...selectedPage,
          instrument_layout: updatedLayout,
        },
      }),
    );
  };

  const handleDelete = (instrumentData: any) => {
    dispatch(deleteInstrument({ chartId: instrumentData.id })).then(() => {
      dispatch(
        setAllInstruments(
          allInstruments.filter((instrument: any) => instrument.id !== instrumentData.id),
        ),
      );

      let currentLayout = {};
      Object.keys(savedLayout).map((breakpoint: any) => {
        currentLayout = {
          ...currentLayout,
          [breakpoint]: savedLayout[breakpoint].filter((item: any) => {
            return item.i !== String(instrumentData.id);
          }),
        };
        return breakpoint;
      });

      setSavedLayout(currentLayout);
      dispatch(
        updatePage({
          request_body: {
            ...selectedPage,
            instrument_layout: currentLayout,
          },
        }),
      );
      setInstrumentDeleteData(null);
    });
  };

  return (
    <>
      <Box width='100%'>
        <ResponsiveReactGridLayout
          resizeHandles={['ne', 'nw', 'se', 'sw']}
          useCSSTransforms
          className='dashboard-grid-container'
          draggableHandle='.dashboard-drag-header'
          allowOverlap={false}
          layouts={savedLayout}
          preventCollision={false}
          // rowHeight={rowHeightMap[currentBreakpoint] || rowHeight}
          rowHeight={rowHeight}
          autoSize={false}
          onBreakpointChange={(newBreakpoint) =>
            setCurrentBreakpoint(newBreakpoint as keyof typeof cols)
          }
          margin={[12, 12]}
          // react-grid-layout defaults containerPadding to the margin value
          // when unset, which added its own 12px on top of the parent Box's
          // px={3} — the grid sat further in than the header above it.
          // Zeroing this leaves only the parent's 12px, matching the header.
          containerPadding={[0, 0]}
          onResizeStop={onResizeStop}
          onDragStop={onDragStop}
          breakpoints={breakpoints}
          cols={cols}
        >
          {savedLayout[currentBreakpoint]?.map((layoutItem) => {
            const item = allInstruments.find((data) => String(data.id) === layoutItem.i);
            if (!item) return null;

            return (
              <Box
                id={`instrument-${item.id}`}
                className='dashboard-item grid-item page-break-before'
                key={item.id}
                data-grid={{ ...layoutItem, isResizable: true, isDraggable: true }}
              >
                <Instrument
                  instrumentData={item}
                  deleteInstrument={() => setInstrumentDeleteData(item)}
                />
              </Box>
            );
          })}
        </ResponsiveReactGridLayout>
      </Box>
      <DeleteModal
        isOpen={instrumentDeleteData}
        onClose={() => setInstrumentDeleteData(null)}
        itemName={instrumentDeleteData?.name}
        onDelete={() => handleDelete(instrumentDeleteData)}
      />
    </>
  );
};

export default Grid;
